import React, { useMemo } from "react";
import { Flame, PlayCircle } from "lucide-react";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Build a conic-gradient string for the donut chart.
 * Each of the 4 categories occupies 90° (25%) of the circle.
 * Within each 90°, the colored arc = completion %; rest is faded.
 */
function buildConicGradient(categories) {
  const gap = 5; // degrees between segments
  const total = categories.length;
  const segDeg = (360 - total * gap) / total; // ≈83.75°
  const parts = [];
  let pos = 0;

  for (const cat of categories) {
    const pct = cat.total > 0 ? Math.min(1, cat.done / cat.total) : 0;
    const filled = pct * segDeg;
    const empty = segDeg - filled;

    // The unfilled remainder is the same hue at 16 % rather than a `28` alpha
    // suffix concatenated onto a hex — which is what tied this to a literal.
    if (filled > 0.1) parts.push(`hsl(var(${cat.token})) ${pos}deg ${pos + filled}deg`);
    if (empty > 0.1)
      parts.push(`hsl(var(${cat.token}) / 0.16) ${pos + filled}deg ${pos + segDeg}deg`);
    if (gap > 0)
      parts.push(`var(--card-bg) ${pos + segDeg}deg ${pos + segDeg + gap}deg`);
    pos += segDeg + gap;
  }
  return `conic-gradient(from -90deg, ${parts.join(", ")})`;
}

/**
 * LearningSidebar — right sidebar with:
 *   • Continue Learning (currently selected session)
 *   • Upcoming Classes (next scheduled/available sessions)
 *   • My Progress donut chart
 *   • My Streak calendar
 */
const LearningSidebar = ({
  modules,
  summary,
  gamification,
  selectedSession,
  onOpenSession,
}) => {
  const allSessions = useMemo(
    () => (modules || []).flatMap((m) => m.sessions || []),
    [modules],
  );

  // ── Stats ──────────────────────────────────────────────────────────────
  const totalClasses = summary?.totalSessions || 0;
  const doneClasses =
    (summary?.completedSessions || 0) + (summary?.masteredSessions || 0);
  const overallPct = summary?.overallProgressPercent || 0;

  const totalAssignments = allSessions.reduce(
    (acc, s) => acc + (s.resourceCounts?.assignment || 0),
    0,
  );
  const doneAssignments = allSessions.filter(
    (s) => s.student?.assignmentCompleted,
  ).length;
  const totalCoding = allSessions.reduce(
    (acc, s) => acc + (s.resourceCounts?.codingChallenge || 0),
    0,
  );
  const doneCoding = allSessions.filter((s) => s.student?.codingCompleted).length;
  const totalQuizzes = allSessions.reduce(
    (acc, s) => acc + (s.resourceCounts?.quiz || 0),
    0,
  );
  const doneQuizzes = allSessions.filter((s) => s.student?.quizCompleted).length;

  // ── Upcoming classes — next 3 non-completed sessions ──────────────────
  const upcoming = useMemo(
    () =>
      allSessions
        .filter((s) => {
          const st = s.student?.state;
          return (
            st === "available" ||
            st === "live" ||
            (!["completed", "mastered", "locked"].includes(st) &&
              s.status === "scheduled")
          );
        })
        .slice(0, 3),
    [allSessions],
  );

  // Find module for each upcoming session (for display)
  const upcomingWithMod = useMemo(
    () =>
      upcoming.map((s) => {
        const mod = modules.find((m) =>
          (m.sessions || []).some((ss) => ss._id === s._id),
        );
        return { session: s, mod };
      }),
    [upcoming, modules],
  );

  // ── Gamification / streak ─────────────────────────────────────────────
  const gam = gamification?.summary;
  const streakDays = gam?.streakDays || 0;
  const lastActivity = gam?.lastActivityAt ? new Date(gam.lastActivityAt) : null;

  const weekDays = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (6 - i));
      const diffMs = lastActivity ? today - d : Infinity;
      const diffDays = Math.floor(diffMs / 86400000);
      const isActive = lastActivity && streakDays > 0 && diffDays < streakDays;
      return { label: DAY_LABELS[d.getDay()], isActive };
    });
  }, [streakDays, lastActivity]);

  // ── Donut chart ───────────────────────────────────────────────────────
  const donutCategories = [
    // Four categories, four chart hues — themed, so the donut has a dark mode.
    { label: "Classes", done: doneClasses, total: totalClasses || 1, token: "--chart-1" },
    { label: "Assignments", done: doneAssignments, total: totalAssignments || 1, token: "--chart-6" },
    { label: "Challenges", done: doneCoding, total: totalCoding || 1, token: "--chart-2" },
    { label: "Quizzes", done: doneQuizzes, total: totalQuizzes || 1, token: "--chart-5" },
  ];
  const conicGrad = buildConicGradient(donutCategories);

  return (
    <aside className="lp-sidebar">
      {/* ── Continue Learning ──────────────────────────────────────────── */}
      {selectedSession && (
        <div className="lp-sidebar__card">
          <div className="lp-sidebar__card-head">
            <h4 className="lp-sidebar__card-title">Continue Learning</h4>
          </div>
          <div className="lp-sidebar__thumb">
            <PlayCircle size={28} className="lp-sidebar__thumb-icon" />
          </div>
          <div className="lp-sidebar__continue-info">
            <span className="lp-sidebar__continue-title">
              {selectedSession.title}
            </span>
            <span className="lp-sidebar__continue-sub">
              {selectedSession.moduleTitle} · Module{" "}
              {selectedSession.moduleNumber}
            </span>
            <div className="lp-sidebar__prog-wrap">
              <div className="lp-sidebar__prog-bar">
                <div
                  className="lp-sidebar__prog-fill"
                  style={{
                    width: `${selectedSession.student?.progressPercent || 0}%`,
                  }}
                />
              </div>
              <span className="lp-sidebar__prog-pct">
                {selectedSession.student?.progressPercent || 0}%
              </span>
            </div>
          </div>
          <button
            type="button"
            className="lp-sidebar__continue-btn"
            onClick={() => onOpenSession && onOpenSession(selectedSession)}
          >
            <PlayCircle size={13} /> Continue Watching
          </button>
        </div>
      )}

      {/* ── Upcoming Classes ──────────────────────────────────────────── */}
      {upcomingWithMod.length > 0 && (
        <div className="lp-sidebar__card">
          <div className="lp-sidebar__card-head">
            <h4 className="lp-sidebar__card-title">Upcoming Classes</h4>
          </div>
          <div className="lp-sidebar__upcoming">
            {upcomingWithMod.map(({ session: s, mod }) => (
              <div key={s._id} className="lp-sidebar__upcoming-row">
                <div className="lp-sidebar__upcoming-label">
                  <span className="lp-sidebar__upcoming-num">
                    {mod?.moduleNumber}.{s.order}
                  </span>
                </div>
                <div className="lp-sidebar__upcoming-info">
                  <span className="lp-sidebar__upcoming-title">{s.title}</span>
                  <span className="lp-sidebar__upcoming-dur">
                    {s.plannedDurationMinutes} min session
                  </span>
                </div>
                <button
                  type="button"
                  className={`lp-sidebar__join-btn ${s.student?.state === "live" ? "lp-sidebar__join-btn--live" : ""}`}
                  onClick={() => onOpenSession && onOpenSession(s)}
                >
                  {s.student?.state === "live" ? "Join" : "View"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── My Progress donut ─────────────────────────────────────────── */}
      <div className="lp-sidebar__card">
        <div className="lp-sidebar__card-head">
          <h4 className="lp-sidebar__card-title">My Progress</h4>
        </div>
        <div className="lp-sidebar__progress-layout">
          <div className="lp-sidebar__donut-wrap">
            <div
              className="lp-sidebar__donut"
              style={{ background: conicGrad }}
              role="img"
              aria-label={`${overallPct}% overall progress`}
            >
              <div className="lp-sidebar__donut-hole">
                <span className="lp-sidebar__donut-pct">{overallPct}%</span>
                <span className="lp-sidebar__donut-label">Overall</span>
              </div>
            </div>
          </div>
          <div className="lp-sidebar__legend">
            {donutCategories.map((cat) => (
              <div key={cat.label} className="lp-sidebar__legend-row">
                <span
                  className="lp-sidebar__legend-dot"
                  style={{ background: `hsl(var(${cat.token}))` }}
                />
                <span className="lp-sidebar__legend-label">{cat.label}</span>
                <span className="lp-sidebar__legend-val">
                  {cat.done} /{" "}
                  {cat.total === 1 && cat.done === 0 ? 0 : cat.total}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── My Streak ─────────────────────────────────────────────────── */}
      {streakDays > 0 && (
        <div className="lp-sidebar__card">
          <div className="lp-sidebar__streak-head">
            <Flame size={18} className="lp-sidebar__streak-flame" />
            <span className="lp-sidebar__streak-num">{streakDays} Day Streak</span>
          </div>
          <div className="lp-sidebar__week">
            {weekDays.map((d, i) => (
              <div
                key={i}
                className={`lp-sidebar__day ${d.isActive ? "lp-sidebar__day--active" : ""}`}
              >
                {d.isActive && <span className="lp-sidebar__day-check">✓</span>}
                <span className="lp-sidebar__day-name">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

export default LearningSidebar;
